import { useState, useEffect } from 'react';
import { X, Package, Star, Upload, Image as ImageIcon, Link, Search, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
export const BASE_URL = import.meta.env.VITE_API_BASE_URL;

interface AddProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  categoryId: string;
  categoryName: string;
  onProductAdded: () => void;
  editProduct?: any; // Add edit product prop
}

type ImageSource = 'upload' | 'stock' | 'url';

interface ImageData {
  url: string;
  source: ImageSource;
  metadata?: {
    unsplashId?: string;
    unsplashAuthor?: string;
    uploadedFilename?: string;
  };
}

// Predefined food categories for quick search
const FOOD_CATEGORIES = [
  'pizza', 'burger', 'pasta', 'sushi', 'salad', 'steak',
  'dessert', 'coffee', 'cocktail', 'seafood', 'breakfast', 'sandwich',
  'tacos', 'noodles', 'rice', 'soup', 'chicken', 'vegetarian'
];

export function AddProductModal({ 
  isOpen, 
  onClose, 
  categoryId, 
  categoryName,
  onProductAdded,
  editProduct
}: AddProductModalProps) {
  const isEditMode = !!editProduct;
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    sku: '',
    isActive: true,
    isFeatured: false,
  });
  
  // Image state
  const [imageTab, setImageTab] = useState<ImageSource>('stock');
  const [selectedImage, setSelectedImage] = useState<ImageData | null>(editProduct?.images?.length > 0 ? { url: editProduct?.images[0], source: 'stock' } : null);
  const [manualUrl, setManualUrl] = useState('');
  
  // Stock library state
  const [stockSearchQuery, setStockSearchQuery] = useState('');
  const [stockImages, setStockImages] = useState<any[]>([]);
  const [loadingStock, setLoadingStock] = useState(false);
  
  const [saving, setSaving] = useState(false);

  // Initialize form data when editProduct changes
  useEffect(() => {
    if (editProduct && isOpen) {
      setFormData({
        name: editProduct.name || '',
        description: editProduct.description || '',
        price: editProduct.price?.toString() || '',
        sku: editProduct.sku || '',
        isActive: editProduct.isActive ?? true,
        isFeatured: editProduct.isFeatured ?? false,
      });
      
      if (editProduct.images?.length > 0) {
        setSelectedImage({
          url: editProduct.images[0],
          source: (editProduct.imageSource as ImageSource) || 'stock',
          metadata: editProduct.imageMetadata,
        });
      }
    }
  }, [editProduct, isOpen]);

  if (!isOpen) return null;

  // Search Unsplash for stock images
  const searchStockImages = async (query: string) => {
    if (!query.trim()) {
      setStockImages([]);
      return;
    }

    setLoadingStock(true);
    try {
      // Simulate Unsplash API call
      // In production, this would call your backend which calls Unsplash API
      const response = await fetch(`https://api.unsplash.com/search/photos?query=${encodeURIComponent(query)}&per_page=12&orientation=landscape`, {
        headers: {
          'Authorization': `Client-ID ${import.meta.env.VITE_UNSPLASH_ACCESS_KEY}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStockImages(data.results || []);
      } else {
        // Fallback to mock data for demo
        setStockImages(generateMockImages(query));
      }
    } catch (error) {
      console.error('Error searching stock images:', error);
      // Use mock data as fallback
      setStockImages(generateMockImages(query));
    } finally {
      setLoadingStock(false);
    }
  };

  // Generate mock images for demo purposes
  const generateMockImages = (query: string) => {
    return Array.from({ length: 12 }, (_, i) => ({
      id: `unsplash-${query}-${i}`,
      urls: {
        regular: `https://images.unsplash.com/photo-${1500000000000 + i}?w=600&h=400&fit=crop&q=80`,
        small: `https://images.unsplash.com/photo-${1500000000000 + i}?w=400&h=300&fit=crop&q=80`,
      },
      user: {
        name: `Photographer ${i + 1}`,
      },
      alt_description: `${query} ${i + 1}`,
    }));
  };

  const selectStockImage = (image: any) => {
    setSelectedImage({
      url: image.urls.regular,
      source: 'stock',
      metadata: {
        unsplashId: image.id,
        unsplashAuthor: image.user.name,
      }
    });
  };

  const selectManualUrl = () => {
    if (!manualUrl.trim()) {
      toast.error('Please enter a valid image URL');
      return;
    }

    // Basic URL validation
    try {
      new URL(manualUrl);
      setSelectedImage({
        url: manualUrl,
        source: 'url',
      });
      toast.success('Image URL added');
    } catch {
      toast.error('Please enter a valid URL');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Please enter a product name');
      return;
    }
    
    if (!formData.price || parseFloat(formData.price) <= 0) {
      toast.error('Please enter a valid price');
      return;
    }

    if (!formData.sku.trim()) {
      toast.error('Please enter a SKU');
      return;
    }

    try {
      setSaving(true);
      
      const url = isEditMode 
        ? `${BASE_URL}/products/${editProduct.id}`
        : `${BASE_URL}/products`;
      
      const method = isEditMode ? 'PATCH' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify({
          categoryId,
          name: formData.name,
          description: formData.description || undefined,
          price: parseFloat(formData.price),
          sku: formData.sku,
          // Send image data
          images: selectedImage ? [selectedImage.url] : [],
          imageSource: selectedImage?.source || null,
          imageMetadata: selectedImage?.metadata || null,
          isActive: formData.isActive,
          isFeatured: formData.isFeatured,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        
        // Handle authentication errors specifically
        if (response.status === 401) {
          toast.error('Your session has expired. Please login again.', {
            duration: 5000,
          });
          setTimeout(() => {
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');
            window.location.reload();
          }, 2000);
          return;
        }
        
        throw new Error(errorData.message || `Failed to ${isEditMode ? 'update' : 'create'} product`);
      }

      toast.success(`Product ${isEditMode ? 'updated' : 'added'} successfully!`);
      onProductAdded();
      handleClose();
    } catch (error: any) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} product:`, error);
      
      if (!error.message?.includes('session has expired')) {
        toast.error(error.message || `Failed to ${isEditMode ? 'update' : 'create'} product`);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      sku: '',
      isActive: true,
      isFeatured: false,
    });
    setSelectedImage(null);
    setManualUrl('');
    setStockSearchQuery('');
    setStockImages([]);
    setImageTab('stock');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {isEditMode ? 'Edit Item' : 'Add New Item'}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {isEditMode ? `Update item in ${categoryName}` : `Add item to ${categoryName}`}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column - Product Details */}
            <div className="space-y-5">
              {/* Product Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Product Name *
                </label>
                <div className="relative">
                  <Package className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Margherita Pizza"
                    className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              {/* SKU */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  SKU *
                </label>
                <input
                  type="text"
                  value={formData.sku}
                  onChange={(e) => setFormData(prev => ({ ...prev, sku: e.target.value }))}
                  placeholder="e.g., PIZZA-001"
                  className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900 dark:text-white"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Enter product description..."
                  rows={3}
                  className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900 dark:text-white resize-none"
                />
              </div>

              {/* Price */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Price *
                </label>
                <div className="relative">
                  <Package className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.price}
                    onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                    placeholder="0.00"
                    className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              {/* Checkboxes */}
              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                    className="w-5 h-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Active (visible to customers)
                  </span>
                </label>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.isFeatured}
                    onChange={(e) => setFormData(prev => ({ ...prev, isFeatured: e.target.checked }))}
                    className="w-5 h-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <div className="flex items-center gap-2">
                    <Star className="w-4 h-4 text-yellow-500" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Featured Product
                    </span>
                  </div>
                </label>
              </div>
            </div>

            {/* Right Column - Image Selection */}
            <div className="space-y-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Product Image
              </label>

              {/* Image Tabs */}
              <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => setImageTab('stock')}
                  className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors ${
                    imageTab === 'stock'
                      ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  <ImageIcon className="w-4 h-4" />
                  Stock Library
                </button>
                <button
                  type="button"
                  onClick={() => setImageTab('upload')}
                  className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors ${
                    imageTab === 'upload'
                      ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  <Upload className="w-4 h-4" />
                  Upload
                </button>
                <button
                  type="button"
                  onClick={() => setImageTab('url')}
                  className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors ${
                    imageTab === 'url'
                      ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  <Link className="w-4 h-4" />
                  URL
                </button>
              </div>

              {/* Tab Content */}
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 min-h-[400px]">
                {/* Stock Library Tab */}
                {imageTab === 'stock' && (
                  <div className="space-y-4">
                    {/* Search */}
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        value={stockSearchQuery}
                        onChange={(e) => {
                          setStockSearchQuery(e.target.value);
                          if (e.target.value.length > 2) {
                            searchStockImages(e.target.value);
                          }
                        }}
                        placeholder="Search food images..."
                        className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900 dark:text-white"
                      />
                    </div>

                    {/* Quick Categories */}
                    <div className="flex flex-wrap gap-2">
                      {FOOD_CATEGORIES.map((category) => (
                        <button
                          key={category}
                          type="button"
                          onClick={() => {
                            setStockSearchQuery(category);
                            searchStockImages(category);
                          }}
                          className="px-3 py-1 text-xs bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-full hover:bg-indigo-50 dark:hover:bg-indigo-900/20 hover:border-indigo-500 transition-colors text-gray-700 dark:text-gray-300"
                        >
                          {category}
                        </button>
                      ))}
                    </div>

                    {/* Image Grid */}
                    {loadingStock ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
                      </div>
                    ) : stockImages.length > 0 ? (
                      <div className="grid grid-cols-3 gap-2 max-h-[280px] overflow-y-auto">
                        {stockImages.map((image) => (
                          <button
                            key={image.id}
                            type="button"
                            onClick={() => selectStockImage(image)}
                            className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                              selectedImage?.metadata?.unsplashId === image.id
                                ? 'border-indigo-500 ring-2 ring-indigo-500'
                                : 'border-transparent hover:border-indigo-300'
                            }`}
                          >
                            <img
                              src={image.urls.small}
                              alt={image.alt_description}
                              className="w-full h-full object-cover"
                            />
                            {selectedImage?.metadata?.unsplashId === image.id && (
                              <div className="absolute inset-0 bg-indigo-500/20 flex items-center justify-center">
                                <div className="w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center">
                                  <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-12 text-gray-500 dark:text-gray-400">
                        <ImageIcon className="w-12 h-12 mb-3 opacity-50" />
                        <p className="text-sm">Search for food images</p>
                        <p className="text-xs">Try: pizza, burger, sushi...</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Upload Tab */}
                {imageTab === 'upload' && (
                  <div className="flex flex-col items-center justify-center h-full text-gray-500 dark:text-gray-400">
                    <Upload className="w-16 h-16 mb-4 opacity-50" />
                    <p className="text-sm font-medium mb-2">Image Upload</p>
                    <p className="text-xs text-center max-w-xs mb-4">
                      Image upload requires backend file storage configuration.
                    </p>
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4 max-w-sm">
                      <p className="text-xs text-yellow-800 dark:text-yellow-200">
                        <strong>Coming Soon:</strong> Drag & drop image upload with automatic optimization and S3/local storage support.
                      </p>
                    </div>
                  </div>
                )}

                {/* URL Tab */}
                {imageTab === 'url' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Image URL
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="url"
                          value={manualUrl}
                          onChange={(e) => setManualUrl(e.target.value)}
                          placeholder="https://example.com/image.jpg"
                          className="flex-1 px-4 py-2.5 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900 dark:text-white"
                        />
                        <button
                          type="button"
                          onClick={selectManualUrl}
                          className="px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
                        >
                          Add
                        </button>
                      </div>
                    </div>

                    {selectedImage?.source === 'url' && (
                      <div className="mt-4">
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Preview:</p>
                        <div className="relative aspect-video rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
                          <img
                            src={selectedImage.url}
                            alt="Preview"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = 'https://via.placeholder.com/400x300?text=Invalid+Image+URL';
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Selected Image Preview */}
              {selectedImage && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded overflow-hidden border border-green-300 dark:border-green-600">
                        <img src={selectedImage.url} alt="" className="w-full h-full object-cover" />
                      </div>
                      <div>
                        <p className="text-xs font-medium text-green-800 dark:text-green-200">
                          Image Selected
                        </p>
                        <p className="text-xs text-green-600 dark:text-green-400">
                          Source: {selectedImage.source === 'stock' ? 'Stock Library' : selectedImage.source === 'upload' ? 'Uploaded' : 'Manual URL'}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSelectedImage(null)}
                      className="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={handleClose}
            disabled={saving}
            className="px-4 py-2.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors font-medium disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || !formData.name.trim() || !formData.price || !formData.sku.trim()}
            className="px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (isEditMode ? 'Updating...' : 'Adding...') : (isEditMode ? 'Update Product' : 'Add Product')}
          </button>
        </div>
      </div>
    </div>
  );
}